import { FileText, FolderCheck, Sparkles, ShieldCheck, FileSignature } from "lucide-react";

const ADMISSION_BG =
    "https://images.unsplash.com/photo-1606761568499-6d2451b23c66?auto=format&fit=crop&w=1600&q=70";

const STEPS = [
    {
        n: "01",
        icon: FileText,
        title: "Registration",
        body: "Submit a filled-up application form along with the prospectus and required documents. Photocopies of the form will not be accepted.",
    },
    {
        n: "02",
        icon: FolderCheck,
        title: "Documentation",
        body: "Submit photo, ID proof, qualification proof, address proof and rank details (if through counselling).",
    },
    {
        n: "03",
        icon: Sparkles,
        title: "Begin the Journey",
        body: "Once procedures are complete, your journey at Prasad Rudrik College of Pharmaceutical Sciences begins.",
    },
];

const ELIGIBILITY = [
    "Age: 17 years or above by 31st December of admission year",
    "10+2 with Physics, Chemistry, Biology or Mathematics (WBCHSE / equivalent)",
    "Vocational stream candidates are not eligible (PCI norms)",
    "Must be medically fit and an Indian citizen",
    "Able to read, write and express in Bengali and English",
];

const CHECKLIST = [
    "10th Standard Admit Card",
    "10th Pass Mark Sheet & Certificate",
    "12th Standard Admit Card",
    "12th Pass Mark Sheet & Certificate",
    "Aadhaar Card",
    "Caste Certificate (for SC/ST/OBC)",
    "Domicile Certificate (other states)",
    "4 Passport Size Photographs",
    "WB Pharmacy Entrance Admit / Rank Card (if applicable)",
];

export const Admission = () => (
    <section
        id="admission"
        data-testid="admission-section"
        className="section-bg relative py-24 lg:py-32 bg-stone-50"
    >
        <div
            className="section-bg__media"
            style={{ backgroundImage: `url('${ADMISSION_BG}')` }}
            aria-hidden="true"
        />
        <div className="section-bg__veil veil-admission" aria-hidden="true" />
        <div className="blob blob--green" style={{ width: 320, height: 320, top: 40, right: -120 }} aria-hidden="true" />
        <div className="blob blob--blue" style={{ width: 260, height: 260, bottom: 80, left: -100, animationDelay: "2.5s" }} aria-hidden="true" />

        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 relative">
            <div className="text-center max-w-2xl mx-auto reveal">
                <span className="section-rule eyebrow mx-auto">Admission</span>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05] text-stone-900">
                    Three steps to start your <em className="not-italic text-[var(--brand-green)]">pharmacy journey</em>.
                </h2>
            </div>

            {/* Timeline */}
            <div className="mt-16 grid md:grid-cols-3 gap-6 lg:gap-8 relative">
                {STEPS.map(({ n, icon: Icon, title, body }, i) => (
                    <div
                        key={title}
                        data-testid={`admission-step-${i + 1}`}
                        className={`tactile-card p-7 md:p-8 reveal delay-${i + 1} relative`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="font-display text-5xl text-stone-200 leading-none">{n}</span>
                            <div className="w-11 h-11 rounded-full flex items-center justify-center bg-[var(--brand-green)] text-white">
                                <Icon size={20} />
                            </div>
                        </div>
                        <h3 className="font-display text-2xl mt-5 text-stone-900">{title}</h3>
                        <p className="mt-3 text-sm md:text-[15px] text-stone-600 leading-relaxed">{body}</p>
                    </div>
                ))}
            </div>

            {/* Eligibility + Checklist */}
            <div className="mt-16 grid lg:grid-cols-2 gap-6 lg:gap-8">
                <div className="tactile-card p-8 md:p-10 reveal">
                    <div className="flex items-center gap-3">
                        <ShieldCheck size={22} className="text-[var(--brand-green)]" />
                        <h3 className="font-display text-2xl md:text-3xl text-stone-900">Eligibility Criteria</h3>
                    </div>
                    <ul className="mt-6 space-y-4">
                        {ELIGIBILITY.map((e) => (
                            <li key={e} className="flex gap-3 items-start text-stone-700 text-sm md:text-[15px]">
                                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[var(--brand-green)] flex-shrink-0" />
                                <span>{e}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="tactile-card p-8 md:p-10 reveal delay-2">
                    <div className="flex items-center gap-3">
                        <FileSignature size={22} className="text-[var(--brand-blue)]" />
                        <h3 className="font-display text-2xl md:text-3xl text-stone-900">Document Checklist</h3>
                    </div>
                    <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                        {CHECKLIST.map((c) => (
                            <li
                                key={c}
                                className="flex gap-2.5 items-start text-stone-700 text-sm leading-relaxed"
                            >
                                <i className="fa-solid fa-check text-[10px] mt-1.5 text-[var(--brand-blue)]" />
                                <span>{c}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="mt-12 text-center reveal">
                <p className="text-stone-600">
                    For any query, contact{" "}
                    <a href="tel:8637584851" className="text-[var(--brand-green)] font-medium link-underline">
                        8637584851
                    </a>{" "}
                    /{" "}
                    <a href="tel:8509650147" className="text-[var(--brand-green)] font-medium link-underline">
                        8509650147
                    </a>
                </p>
            </div>
        </div>
    </section>
);

export default Admission;
